import React, { useRef, useState , useEffect } from "react";
import { useHistory } from "react-router-dom";
import "./FacteurEditeur.css";
import ENTRELOGO from "../images/ENTRELOGO.png";
import { supabase } from "../cloudflareClient";
import html2pdf from "html2pdf.js";
import Header from "../components/Header";

import { 
  AiOutlineDelete, 
  AiOutlinePlus, 
  AiOutlineUser, 
  AiOutlineFileText, 
  AiOutlineCalendar, 
  AiOutlineEdit,
  AiOutlinePrinter,
  AiOutlineDownload,
  AiOutlineSave,
  AiOutlineCheck
} from "react-icons/ai";


function FacteurEditeur() {
  // --- HOOKS: All useState, useRef, useEffect at the top ---
  const componentRef = useRef();
  const history = useHistory();

  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");

  // Client management
  const [clients, setClients] = useState([]); // List of saved clients
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showNewClientFields, setShowNewClientFields] = useState(false);

  const [form, setForm] = useState({
    client: "",
    ice: "",
    address: "",
    date: "",
    period: "",
    factureNumber: "",
    items: [{ designation: "", prix: "", qte: "" }]
  });

  const [entrepreneur, setEntrepreneur] = useState(null);
  const [entrepreneurDraft, setEntrepreneurDraft] = useState({
    name: "",
    address: "",
    phone: "",
    cnie: "",
    ice: "",
    if: "",
    tp: ""
  });
  const [userId, setUserId] = useState(null);
  const [editEntrepreneur, setEditEntrepreneur] = useState(false);

  // Load factureToEdit if present
  useEffect(() => {
    const toEdit = localStorage.getItem("factureToEdit");
    if (toEdit) {
      const editedForm = JSON.parse(toEdit);
      setForm(editedForm);
      // Extract dates from form.date if it exists (format: "JJ/MM/YYYY au JJ/MM/YYYY")
      if (editedForm.date && editedForm.date.includes(' au ')) {
        const [start, end] = editedForm.date.split(' au ');
        setDateStart(start.trim());
        setDateEnd(end.trim());
      }
      localStorage.removeItem("factureToEdit");
    }
  }, []);

  // Fetch entrepreneur data for current user
  useEffect(() => {
    const fetchEntrepreneur = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data: profileResult } = await supabase.auth.getProfile();
        const data = profileResult?.profile;
        if (data) {
          setEntrepreneur(data);
          setEntrepreneurDraft({
            name: data.name || "",
            address: data.address || "",
            phone: data.phone || "",
            cnie: data.cnie || "",
            ice: data.ice || "",
            if: data.if || "",
            tp: data.tp || ""
          });
        }
      }
    };
    fetchEntrepreneur();
  }, []);

  // Fetch clients when entrepreneur changes
  useEffect(() => {
    if (entrepreneur?.id) fetchClients(entrepreneur.id);
  }, [entrepreneur]);

  // --- END HOOKS ---

  // Fetch clients for this entrepreneur
  const fetchClients = async (entrepreneurId) => {
    if (!entrepreneurId) return;
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('entrepreneur_id', entrepreneurId)
      .order('id', { ascending: false });
    if (error) {
      console.error("Erreur chargement clients:", error);
      return;
    }
    setClients(data || []);
  };

  // Delete client from Supabase and refetch
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);
      if (error) throw error;
      if (entrepreneur?.id) {
        await fetchClients(entrepreneur.id);
      }
      if (selectedClientId === String(clientId)) {
        setSelectedClientId('');
        setForm(f => ({ ...f, client: '', ice: '', address: '' }));
      }
    } catch (err) {
      alert("Erreur lors de la suppression du client : " + err.message);
    }
  };

  // Save new client to Supabase and refetch
  const handleSaveNewClient = async (e) => {
    e.preventDefault();
    if (!form.client || !form.ice) {
      alert('Nom et ICE du client requis');
      return;
    }
    try {
      let currentEntrepreneur = entrepreneur;
      if (!currentEntrepreneur?.id) {
        const { data: profileResult } = await supabase.auth.getProfile();
        if (profileResult?.profile?.id) {
          currentEntrepreneur = profileResult.profile;
          setEntrepreneur(currentEntrepreneur);
        } else {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: directEnt } = await supabase
              .from('entrepreneur')
              .select('*')
              .eq('user_id', user.id)
              .single();
            if (directEnt?.id) {
              currentEntrepreneur = directEnt;
              setEntrepreneur(directEnt);
            }
          }
        }
      }

      if (!currentEntrepreneur?.id) {
        alert("Veuillez d'abord compléter et enregistrer vos informations auto-entrepreneur ci-dessus.");
        return;
      }

      const { data, error } = await supabase
        .from('clients')
        .insert([
          {
            entrepreneur_id: currentEntrepreneur.id,
            name: form.client,
            ice: form.ice,
            address: form.address || ''
          }
        ])
        .select();
      if (error) throw error;
      await fetchClients(currentEntrepreneur.id);
      if (data && data[0]?.id) {
        setSelectedClientId(String(data[0].id));
      }
      setShowNewClientFields(false);
      alert('Client enregistré !');
    } catch (err) {
      alert("Erreur lors de l'enregistrement du client : " + err.message);
    }
  };

  // Handler for entrepreneur draft fields
  const handleEntrepreneurDraftChange = (e) => {
    const { name, value } = e.target;
    setEntrepreneurDraft((prev) => ({ ...prev, [name]: value }));
  };

  // Save entrepreneur info for the first time or update if exists
  const handleSaveEntrepreneur = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert("Utilisateur non identifié.");
      return;
    }
    // Minimal validation
    if (!entrepreneurDraft.name || !entrepreneurDraft.address) {
      alert("Veuillez remplir au moins le nom et l'adresse.");
      return;
    }

    let error = null;
    if (entrepreneur && entrepreneur.id) {
      // Update existing entrepreneur
      const { error: updateError } = await supabase
        .from("entrepreneur")
        .update({
          name: entrepreneurDraft.name,
          address: entrepreneurDraft.address,
          phone: entrepreneurDraft.phone,
          cnie: entrepreneurDraft.cnie,
          ice: entrepreneurDraft.ice,
          if: entrepreneurDraft.if,
          tp: entrepreneurDraft.tp
        })
        .eq("id", entrepreneur.id);
      error = updateError;
    } else {
      // Insert new entrepreneur
      const { error: insertError } = await supabase.from("entrepreneur").insert([{
        user_id: userId,
        name: entrepreneurDraft.name,
        address: entrepreneurDraft.address,
        phone: entrepreneurDraft.phone,
        cnie: entrepreneurDraft.cnie,
        ice: entrepreneurDraft.ice,
        if: entrepreneurDraft.if,
        tp: entrepreneurDraft.tp
      }]);
      error = insertError;
    }

    if (error) {
      alert("Erreur lors de l'enregistrement: " + error.message);
      return;
    }
    // Reload entrepreneur data
    const { data } = await supabase
      .from("entrepreneur")
      .select("*")
      .eq("user_id", userId)
      .single();
    setEntrepreneur(data);
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    if (name.startsWith("item")) {
      const items = [...form.items];
      items[index][name.split(".")[1]] = value;
      setForm({ ...form, items });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  useEffect(() => {
  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      history.push("/"); // redirect to login
    }
  };
  checkAuth();
}, [history]);

  const addRow = () => {
    setForm({ ...form, items: [...form.items, { designation: "", prix: "", qte: "" }] });
  };

  const removeRow = (index) => {
    if (form.items.length <= 1) return;
    const items = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items });
  };

  const total = form.items.reduce(
    (acc, item) => acc + Number(item.prix || 0) * Number(item.qte || 0),
    0
  );

  const totalInWords = convertirMontantEnLettres(total);

  function convertirMontantEnLettres(n) {
    const unite = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf"];
    const dizaine = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante-dix", "quatre-vingt", "quatre-vingt-dix"];
    const dizaine10_19 = ["dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];

    function convertirNombre(n) {
      if (n < 10) return unite[n];
      if (n < 20) return dizaine10_19[n - 10];
      if (n < 100) {
        let d = Math.floor(n / 10);
        let u = n % 10;
        let sep = (u === 1 && d !== 8) ? " et " : (u > 0 ? "-" : "");
        return dizaine[d] + sep + (u > 0 ? unite[u] : "");
      }
      if (n < 1000) {
        let c = Math.floor(n / 100);
        let r = n % 100;
        let cent = (c === 1 ? "cent" : unite[c] + " cent");
        if (r === 0) return cent;
        return cent + " " + convertirNombre(r);
      }
      if (n < 1000000) {
        let m = Math.floor(n / 1000);
        let r = n % 1000;
        let mille = (m === 1 ? "mille" : convertirNombre(m) + " mille");
        if (r === 0) return mille;
        return mille + " " + convertirNombre(r);
      }
      return "montant trop grand";
    }

    const entier = Math.floor(n);
    const decimal = Math.round((n - entier) * 100);

    let resultat = convertirNombre(entier) + " dirhams";
    if (decimal > 0) {
      resultat += " et " + convertirNombre(decimal) + " centimes";
    }

    return resultat.toUpperCase();
  }

  const handlePrint = () => {
    window.print();
  };

  // Modified handleSubmit to match new schema and exact A4 1-page output
  const handleSubmit = async (e, shouldRedirect = true) => {
    let redirect = shouldRedirect;
    if (typeof e === 'boolean') {
      redirect = e;
    } else if (e && e.preventDefault) {
      e.preventDefault();
    }

    try {
      const {
        client,
        ice,
        address,
        date,
        period,
        factureNumber,
        items
      } = form;

      const total_ht = total;
      const total_letters = totalInWords;

      // 1. Convert to PDF - Exact A4 Single Page
      const opt = {
        margin: 0,
        filename: `facture-${factureNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          windowWidth: 1200,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: 'avoid-all' }
      };

      const pdfBlob = await html2pdf().set(opt).from(componentRef.current).outputPdf('blob');

      // 2. Upload PDF to Supabase Storage
      const filename = `facture-${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('factures-pdf')
        .upload(filename, pdfBlob, {
          contentType: 'application/pdf',
        });

      if (uploadError) throw uploadError;

      const storedPath = uploadData?.path;
      if (!storedPath) throw new Error("Le PDF n'a pas été enregistré.");

      const { data: publicUrl } = supabase
        .storage
        .from('factures-pdf')
        .getPublicUrl(storedPath);

      // 3. Insert metadata into Supabase DB
      let currentEntrepreneur = entrepreneur;
      if (!currentEntrepreneur?.id) {
        const { data: profileResult } = await supabase.auth.getProfile();
        if (profileResult?.profile?.id) {
          currentEntrepreneur = profileResult.profile;
          setEntrepreneur(currentEntrepreneur);
        }
      }

      if (!currentEntrepreneur?.id) {
        alert("Impossible de trouver les informations de l'entrepreneur. Veuillez d'abord enregistrer votre profil.");
        return false;
      }

      const { error: insertError } = await supabase.from("factures").insert([
        {
          entrepreneur_id: currentEntrepreneur.id,
          client,
          ice,
          address,
          date: date ? date : null,
          period,
          facture_number: factureNumber,
          items,
          total_ht,
          total_letters,
          pdf_url: publicUrl.publicUrl
        },
      ]);

      if (insertError) throw insertError;

      if (redirect) {
        alert("Facture enregistrée !");
        history.push("/historique");
      }
      return true;
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err.message);
      alert("Une erreur s'est produite !");
      return false;
    }
  };

  // Submit then print without prematurely unmounting component
  const handleSubmitAndPrint = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const success = await handleSubmit(false);
    if (success) {
      setTimeout(() => {
        handlePrint();
      }, 300);
    }
  };

  // Download PDF directly with exact A4 1-page options
  const handleDownloadPDF = async () => {
    if (!componentRef.current) return;
    
    try {
      let filename = `facture-${form.factureNumber || 'draft'}`;
      if (form.period) {
        filename = `${form.period}-${filename}`.replace(/\s+/g, '_');
      }
      
      const opt = {
        margin: 0,
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          windowWidth: 1200,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: 'avoid-all' }
      };

      await html2pdf().set(opt).from(componentRef.current).save();
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  // Helper to get current year
  const getCurrentYear = () => new Date().getFullYear();

  // Helper to get current date in yyyy-mm-dd format
  const getCurrentDate = () => {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };

  const formatInvoiceDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
  };

  const getNextInvoicePeriod = (value) => {
    const parts = String(value || "").split(" au ");
    const endMatch = (parts[parts.length - 1] || "").match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
    );
    const fallback = new Date();
    const sourceEnd = endMatch
      ? new Date(Number(endMatch[3]), Number(endMatch[2]) - 1, Number(endMatch[1]))
      : fallback;
    const nextMonthStart = new Date(
      sourceEnd.getFullYear(),
      sourceEnd.getMonth() + 1,
      1
    );
    const nextMonthEnd = new Date(
      nextMonthStart.getFullYear(),
      nextMonthStart.getMonth() + 1,
      0
    );

    return {
      date: `${formatInvoiceDate(nextMonthStart)} au ${formatInvoiceDate(nextMonthEnd)}`,
      period: nextMonthStart.toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      }),
    };
  };

  const handleContinueLastInvoice = async () => {
    if (!entrepreneur?.id) {
      alert("Profil entrepreneur introuvable.");
      return;
    }

    const { data: latestInvoices, error } = await supabase
      .from("factures")
      .select("*")
      .eq("entrepreneur_id", entrepreneur.id)
      .order("created_at", { ascending: false });

    if (error) {
      alert("Impossible de charger la dernière facture.");
      return;
    }

    const latestInvoice = latestInvoices?.[0];
    if (!latestInvoice) {
      alert("Aucune facture précédente à poursuivre.");
      return;
    }

    const numberMatch = String(latestInvoice.facture_number || "").match(
      /^(\d+)\/(\d{4})$/
    );
    const nextNumber = numberMatch
      ? `${Number(numberMatch[1]) + 1}/${numberMatch[2]}`
      : "1/" + getCurrentYear();
    const nextPeriod = getNextInvoicePeriod(latestInvoice.date);

    setForm({
      client: latestInvoice.client || "",
      ice: latestInvoice.ice || "",
      address: latestInvoice.address || "",
      date: nextPeriod.date,
      period: nextPeriod.period,
      factureNumber: nextNumber,
      items: Array.isArray(latestInvoice.items)
        ? latestInvoice.items.map((item) => ({ ...item }))
        : [{ designation: "", prix: "", qte: "" }],
    });
    setDateStart(nextPeriod.date.split(" au ")[0]);
    setDateEnd(nextPeriod.date.split(" au ")[1]);
    setSelectedClientId("");
    setShowNewClientFields(false);
  };

  // Automatically set facture number and date if not editing
  useEffect(() => {
    const toEdit = localStorage.getItem("factureToEdit");
    if (toEdit) return; // Don't auto-set if editing

    const fetchNextFactureNumber = async () => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      // Get entrepreneur for user
      const { data: entrepreneur } = await supabase
        .from("entrepreneur")
        .select("id")
        .eq("user_id", user.id)
        .single();
      if (!entrepreneur) return;
      // Get max facture_number for current year
      const year = getCurrentYear();
      const { data: factures } = await supabase
        .from("factures")
        .select("facture_number")
        .eq("entrepreneur_id", entrepreneur.id)
        .like("facture_number", `%/${year}`);
      let maxNum = 0;
      if (factures && factures.length > 0) {
        factures.forEach(f => {
          const match = f.facture_number && f.facture_number.match(/^(\d+)\//);
          if (match) {
            const num = parseInt(match[1], 10);
            if (num > maxNum) maxNum = num;
          }
        });
      }
      const nextNum = maxNum + 1;
      setForm(prev => ({
        ...prev,
        factureNumber: `${nextNum}/${year}`,
        date: getCurrentDate()
      }));
    };
    fetchNextFactureNumber();
  }, []);


  // Helper to convert date string (JJ/MM/YYYY) to month name and year
  const getMonthYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return { month, year, monthName: months[month - 1] };
  };

  // Handle date range change for start and end
  const handleDateChange = (start, end) => {
    setDateStart(start);
    setDateEnd(end);
    
    let period = '';
    const startInfo = getMonthYearFromDate(start);
    const endInfo = getMonthYearFromDate(end);

    if (startInfo && endInfo) {
      if (startInfo.month === endInfo.month && startInfo.year === endInfo.year) {
        // Same month
        period = `${startInfo.monthName} ${startInfo.year}`;
      } else if (startInfo.year === endInfo.year) {
        // Same year, different months
        period = `${startInfo.monthName} à ${endInfo.monthName} ${endInfo.year}`;
      } else {
        // Different years
        period = `${startInfo.monthName} ${startInfo.year} à ${endInfo.monthName} ${endInfo.year}`;
      }
    }

    setForm((prev) => ({
      ...prev,
      date: `${start} au ${end}`,
      period: period,
    }));
  };

  // Mask handler for facture number
  const handleFactureNumberChange = (e) => {
    let value = e.target.value.replace(/[^\d]/g, ""); // Only digits
    let year = getCurrentYear();
    // If value already contains year, extract it
    if (value.length > 4) {
      const possibleYear = value.slice(-4);
      if (/^20\d{2}$/.test(possibleYear)) {
        year = possibleYear;
        value = value.slice(0, -4);
      }
    }
    // Remove leading zeros
    value = value.replace(/^0+/, "");
    // Compose mask n/année
    let masked = value ? `${value}/${year}` : "";
    setForm((prev) => ({ ...prev, factureNumber: masked }));
  };

  return (
    <div className="App">
      {/* Separated Pro Header Component */}
      <Header activePage="facteur-editeur" />

      {/* Editor Greeting & Title */}
      <div className="editor-banner">
      
        <h1 className="banner-heading">Créateur de Facture</h1>
        <p className="banner-subtext">Remplissez les informations ci-dessous pour générer votre facture A4 conforme.</p>
        <button
          type="button"
          className="continue-invoice-btn"
          onClick={handleContinueLastInvoice}
          disabled={!entrepreneur}
        >
          <AiOutlineFileText />
          Poursuivre la dernière facture
        </button>
      </div>

      <form className="form-zone" onSubmit={handleSubmit}>
        {/* CARD 1: CLIENTS ENREGISTRÉS */}
        <section className="form-card">
          <div className="card-header">
            <div className="card-title">
              <AiOutlineUser className="section-icon" />
              <span>Clients enregistrés</span>
            </div>
            <span className="card-badge">{clients.length} client{clients.length > 1 ? 's' : ''}</span>
          </div>

          <div className="card-body">
            <div className="client-chips-wrapper">
              {clients.length === 0 && (
                <span className="no-clients-text">Aucun client enregistré pour l'instant.</span>
              )}
              {clients.map(c => {
                const isSelected = selectedClientId === String(c.id);
                return (
                  <div 
                    key={c.id} 
                    className={`client-chip ${isSelected ? 'client-chip-selected' : ''}`}
                    onClick={() => {
                      setShowNewClientFields(false);
                      setSelectedClientId(String(c.id));
                      setForm(f => ({ ...f, client: c.name, ice: c.ice, address: c.address }));
                    }}
                  >
                    {isSelected && <AiOutlineCheck className="chip-check-icon" />}
                    <span className="chip-name">{c.name}</span>
                    <button
                      type="button"
                      className="btn-chip-delete"
                      title="Supprimer ce client"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClient(c.id);
                      }}
                    >
                      <AiOutlineDelete />
                    </button>
                  </div>
                );
              })}

              <button
                type="button"
                className={`chip-action-btn ${showNewClientFields ? 'chip-action-btn-active' : ''}`}
                onClick={() => {
                  setShowNewClientFields(true);
                  setSelectedClientId('');
                  setForm(f => ({ ...f, client: '', ice: '', address: '' }));
                }}
              >
                <AiOutlinePlus />
                <span>Nouveau client</span>
              </button>

              <button
                type="button"
                className={`chip-action-btn ${editEntrepreneur ? 'chip-action-btn-active' : ''}`}
                onClick={() => {
                  if (entrepreneur) {
                    setEntrepreneurDraft({
                      name: entrepreneur.name || "",
                      address: entrepreneur.address || "",
                      phone: entrepreneur.phone || "",
                      cnie: entrepreneur.cnie || "",
                      ice: entrepreneur.ice || "",
                      if: entrepreneur.if || "",
                      tp: entrepreneur.tp || ""
                    });
                  }
                  setEditEntrepreneur(true);
                }}
              >
                <AiOutlineEdit />
                <span>Modifier mes coordonnées</span>
              </button>
            </div>
          </div>
        </section>

        {/* CARD 2: INFORMATIONS DU CLIENT & DÉTAILS DE FACTURE */}
        <section className="form-card">
          <div className="card-header">
            <div className="card-title">
              <AiOutlineFileText className="section-icon" />
              <span>Informations Client & Détails Facture</span>
            </div>
          </div>

          <div className="card-body">
            {/* If adding a new client */}
            {showNewClientFields && (
              <div className="new-client-panel">
                <div className="panel-title">
                  <AiOutlinePlus />
                  <span>Enregistrer un nouveau client</span>
                </div>
                <div className="form-grid">
                  <div className="input-group">
                    <label>Nom ou Raison Sociale du Client *</label>
                    <input
                      className="effect-1"
                      type="text"
                      name="client"
                      placeholder="Ex: Client SARL..."
                      value={form.client}
                      onChange={e => setForm({ ...form, client: e.target.value })}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label>I.C.E du Client *</label>
                    <input
                      className="effect-1"
                      type="text"
                      name="ice"
                      placeholder="Ex: 001234567890001"
                      value={form.ice}
                      onChange={e => setForm({ ...form, ice: e.target.value })}
                      required
                    />
                  </div>
                  <div className="input-group full-width">
                    <label>Adresse du Client</label>
                    <input
                      className="effect-1"
                      type="text"
                      name="address"
                      placeholder="Ex: 123 Boulevard Mohammed V, Agadir"
                      value={form.address}
                      onChange={e => setForm({ ...form, address: e.target.value })}
                    />
                  </div>
                </div>
                <div className="panel-actions">
                  <button type="button" className="btn-panel-save" onClick={handleSaveNewClient}>
                    <AiOutlineSave />
                    <span>Sauvegarder ce client</span>
                  </button>
                  <button type="button" className="btn-panel-cancel" onClick={() => setShowNewClientFields(false)}>
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {!showNewClientFields && (
              <div className="form-grid">
                <div className="input-group">
                  <label>Nom du Client</label>
                  <input
                    className="effect-1"
                    type="text"
                    name="client"
                    placeholder="Nom du client sélectionné"
                    value={form.client}
                    onChange={e => setForm({ ...form, client: e.target.value })}
                    disabled={!!selectedClientId}
                  />
                </div>
                <div className="input-group">
                  <label>I.C.E du Client *</label>
                  <input
                    className="effect-1"
                    type="text"
                    name="ice"
                    placeholder="Numéro I.C.E"
                    value={form.ice}
                    onChange={e => setForm({ ...form, ice: e.target.value })}
                    disabled={!!selectedClientId}
                    required
                  />
                </div>
                <div className="input-group full-width">
                  <label>Adresse du Client</label>
                  <input
                    className="effect-1"
                    type="text"
                    name="address"
                    placeholder="Adresse complète du client"
                    value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })}
                    disabled={!!selectedClientId}
                  />
                </div>
              </div>
            )}

            <div className="divider-line" />

            {/* Facture Dates, Number, Period */}
            <div className="form-grid">
              <div className="input-group">
                <label htmlFor="dateStart">
                  <AiOutlineCalendar />
                  <span>Date Début</span>
                </label>
                <input
                  className="effect-1"
                  type="text"
                  id="dateStart"
                  name="dateStart"
                  placeholder="JJ/MM/AAAA"
                  value={dateStart}
                  onChange={(e) => handleDateChange(e.target.value, dateEnd)}
                />
              </div>

              <div className="input-group">
                <label htmlFor="dateEnd">
                  <AiOutlineCalendar />
                  <span>Date Fin</span>
                </label>
                <input
                  className="effect-1"
                  type="text"
                  id="dateEnd"
                  name="dateEnd"
                  placeholder="JJ/MM/AAAA"
                  value={dateEnd}
                  onChange={(e) => handleDateChange(dateStart, e.target.value)}
                />
              </div>

              <div className="input-group">
                <label htmlFor="factureNumber">N° Facture</label>
                <input
                  className="effect-1"
                  type="text"
                  id="factureNumber"
                  name="factureNumber"
                  value={form.factureNumber}
                  onChange={handleFactureNumberChange}
                  placeholder={`ex: 1/${getCurrentYear()}`}
                  maxLength={9}
                />
              </div>

              <div className="input-group">
                <label htmlFor="period">Période de Facturation</label>
                <input
                  className="effect-1"
                  type="text"
                  id="period"
                  name="period"
                  value={form.period}
                  onChange={(e) => setForm({ ...form, period: e.target.value })}
                  placeholder="ex: Janvier 2024"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CARD 3: AUTO-ENTREPRENEUR PROFILE */}
        <section className="form-card">
          <div className="card-header">
            <div className="card-title">
              <AiOutlineUser className="section-icon" />
              <span>Coordonnées de l'Auto-Entrepreneur</span>
            </div>
            {!editEntrepreneur && (
              <button
                type="button"
                className="btn-card-action"
                onClick={() => {
                  if (entrepreneur) {
                    setEntrepreneurDraft({
                      name: entrepreneur.name || "",
                      address: entrepreneur.address || "",
                      phone: entrepreneur.phone || "",
                      cnie: entrepreneur.cnie || "",
                      ice: entrepreneur.ice || "",
                      if: entrepreneur.if || "",
                      tp: entrepreneur.tp || ""
                    });
                  }
                  setEditEntrepreneur(true);
                }}
              >
                <AiOutlineEdit />
                <span>Modifier mes données</span>
              </button>
            )}
          </div>

          <div className="card-body">
            {entrepreneur && !editEntrepreneur ? (
              <div className="profile-display-grid">
                <div className="profile-field">
                  <span className="profile-label">Nom et Prénom</span>
                  <span className="profile-val">{entrepreneur.name || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">Téléphone</span>
                  <span className="profile-val">{entrepreneur.phone || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">CNIE</span>
                  <span className="profile-val">{entrepreneur.cnie || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">I.C.E</span>
                  <span className="profile-val">{entrepreneur.ice || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">Identifiant Fiscal (IF)</span>
                  <span className="profile-val">{entrepreneur.if || "—"}</span>
                </div>
                <div className="profile-field">
                  <span className="profile-label">Taxe Professionnelle (TP)</span>
                  <span className="profile-val">{entrepreneur.tp || "—"}</span>
                </div>
                <div className="profile-field full-width">
                  <span className="profile-label">Adresse</span>
                  <span className="profile-val">{entrepreneur.address || "—"}</span>
                </div>
              </div>
            ) : (
              <div className="profile-edit-grid">
                <div className="form-grid">
                  <div className="input-group">
                    <label>Nom et Prénom *</label>
                    <input className="effect-1" type="text" name="name" value={entrepreneurDraft.name} onChange={handleEntrepreneurDraftChange} required />
                  </div>
                  <div className="input-group">
                    <label>Téléphone</label>
                    <input className="effect-1" type="text" name="phone" value={entrepreneurDraft.phone} onChange={handleEntrepreneurDraftChange} />
                  </div>
                  <div className="input-group">
                    <label>CNIE</label>
                    <input className="effect-1" type="text" name="cnie" value={entrepreneurDraft.cnie} onChange={handleEntrepreneurDraftChange} />
                  </div>
                  <div className="input-group">
                    <label>I.C.E</label>
                    <input className="effect-1" type="text" name="ice" value={entrepreneurDraft.ice} onChange={handleEntrepreneurDraftChange} />
                  </div>
                  <div className="input-group">
                    <label>Identifiant Fiscal (IF)</label>
                    <input className="effect-1" type="text" name="if" value={entrepreneurDraft.if} onChange={handleEntrepreneurDraftChange} />
                  </div>
                  <div className="input-group">
                    <label>Taxe Professionnelle (TP)</label>
                    <input className="effect-1" type="text" name="tp" value={entrepreneurDraft.tp} onChange={handleEntrepreneurDraftChange} />
                  </div>
                  <div className="input-group full-width">
                    <label>Adresse professionnelle *</label>
                    <input className="effect-1" type="text" name="address" value={entrepreneurDraft.address} onChange={handleEntrepreneurDraftChange} required />
                  </div>
                </div>

                <div className="panel-actions">
                  <button
                    type="button"
                    className="btn-panel-save"
                    onClick={async (e) => {
                      await handleSaveEntrepreneur(e);
                      setEditEntrepreneur(false);
                    }}
                  >
                    <AiOutlineSave />
                    <span>Enregistrer mes coordonnées</span>
                  </button>
                  {entrepreneur && (
                    <button
                      type="button"
                      className="btn-panel-cancel"
                      onClick={() => setEditEntrepreneur(false)}
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* CARD 4: ARTICLES & PRESTATIONS */}
        <section className="form-card">
          <div className="card-header">
            <div className="card-title">
              <AiOutlineFileText className="section-icon" />
              <span>Articles & Prestations</span>
            </div>
            <span className="card-badge">{form.items.length} ligne{form.items.length > 1 ? 's' : ''}</span>
          </div>

          <div className="card-body">
            <div className="items-table-container">
              {form.items.map((item, index) => (
                <div key={index} className="item-input-row">
                  <div className="input-group item-designation">
                    <label>Désignation / Description</label>
                    <input
                      className="effect-1"
                      type="text"
                      name={`item.designation`}
                      placeholder="Description du produit ou service..."
                      value={item.designation}
                      onChange={(e) => handleChange(e, index)}
                    />
                  </div>

                  <div className="input-group item-qte">
                    <label>Qté</label>
                    <input
                      className="effect-1"
                      type="number"
                      name={`item.qte`}
                      placeholder="1"
                      value={item.qte}
                      onChange={(e) => handleChange(e, index)}
                    />
                  </div>

                  <div className="input-group item-prix">
                    <label>Prix Unitaire (MAD)</label>
                    <input
                      className="effect-1"
                      type="number"
                      name={`item.prix`}
                      placeholder="0.00"
                      value={item.prix}
                      onChange={(e) => handleChange(e, index)}
                    />
                  </div>

                  <div className="item-row-total">
                    <span className="sub-label">Total ligne</span>
                    <span className="amount">
                      {((Number(item.qte) || 0) * (Number(item.prix) || 0)).toFixed(2)} MAD
                    </span>
                  </div>

                  <div className="item-row-action">
                    <button
                      type="button"
                      className="btn-delete-row"
                      title="Supprimer cette ligne"
                      disabled={form.items.length <= 1}
                      onClick={() => removeRow(index)}
                    >
                      <AiOutlineDelete />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className="btn-add-line" onClick={addRow}>
              <AiOutlinePlus />
              <span>Ajouter une ligne</span>
            </button>

            {/* Total Summary Card */}
            <div className="items-summary-card">
              <div className="summary-left">
                <span className="summary-label">ARRÊTÉ À LA SOMME DE :</span>
                <span className="summary-letters">{totalInWords}</span>
              </div>
              <div className="summary-right">
                <span className="summary-total-label">Total Net à payer</span>
                <span className="summary-total-amount">{total.toFixed(2)} MAD</span>
              </div>
            </div>
          </div>
        </section>

        {/* CARD 5: MAIN ACTIONS */}
        <div className="form-actions-bar">
          <button
            type="button"
            className="action-btn-main btn-save-print"
            onClick={handleSubmitAndPrint}
          >
            <AiOutlineSave className="btn-act-icon" />
            <span>Enregistrer et Imprimer</span>
          </button>

          <button
            type="button"
            className="action-btn-main btn-direct-print"
            onClick={handlePrint}
          >
            <AiOutlinePrinter className="btn-act-icon" />
            <span>Imprimer</span>
          </button>

          <button
            type="button"
            className="action-btn-main btn-download-pdf"
            onClick={handleDownloadPDF}
          >
            <AiOutlineDownload className="btn-act-icon" />
            <span>Télécharger PDF</span>
          </button>
        </div>
      </form>

      <div className="invoice-preview-header">
        <div className="invoice-preview-badge">
          Aperçu de la facture
        </div>
        <span className="invoice-preview-hint">
          Glissez horizontalement pour voir toute la largeur
        </span>
      </div>

      <div className="invoice-preview-wrapper">
        <div className="invoice" ref={componentRef}>
          <div className="header">
            <div className="header-spacer" />
            <div className="location-date">
              <p style={{ margin: "2px 0" }}>AGADIR LE : {form.date}</p>
            </div>
          </div>

          <div className="logo-section">
            <img style={{ margin: "2px 0" }} src={ENTRELOGO} alt="Logo" className="logo" />
            <h2 style={{ margin: "2px 0" }}>{entrepreneur ? entrepreneur.name : ""}</h2>
            {/* <p className="sub-logo">Auto-entrepreneur</p> */}
          </div>

          <div className="invoice-details">
            <div >
              <p className="facture-number-box"><strong>Facture numéro :</strong> {form.factureNumber}</p>
            </div>
            <p style={{ margin: "2px 0" }}><strong>Client :</strong> {form.client}</p>
            <p style={{ margin: "2px 0" }}><strong>I.C.E :</strong> {form.ice}</p>
            <p style={{ margin: "2px 0" }}><strong>Adresse :</strong> {form.address}</p>
          </div>

          <table className="invoice-table">
            <thead>
              <tr className="table-header">
                <th>Désignation</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, index) => (
                <tr key={index}>
                  <td>{item.designation}</td>
                  <td>{item.qte}</td>
                  <td>{item.prix}</td>
                  <td>{(item.qte * item.prix).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="footer">
            <p><strong>PÉRIODE :</strong> {form.period}</p>
            <div className="footer-totals">
              <p><strong>Montant en dirhams (Hors champ de la TVA¹)</strong></p>
              <p><strong>Total Net à payer :</strong> {total.toFixed(2)} MAD</p>
            </div>
            <p><strong>ARRÊTE LA PRÉSENTE FACTURE À LA SOMME DE :</strong> {totalInWords}</p>

            <p className="signature-line">Signature :</p>
            
          </div>

          <div className="invoice-bottom-fixed">
            <p className="note">¹Art 89 – II – 1° - c, Code Général des Impôts.</p>
            <div className="bottom-info">
              <div>
                <p style={{ margin: "2px 0" }}><strong>Auto Entrepreneur :</strong> {entrepreneur ? entrepreneur.name : ""}</p>
                <p style={{ margin: "2px 0" }}><strong>Adresse :</strong> {entrepreneur ? entrepreneur.address : ""}</p>
                <p style={{ margin: "2px 0" }}><strong>TEL :</strong> {entrepreneur ? entrepreneur.phone : ""}</p>
                <p style={{ margin: "2px 0" }}><strong>CNIE :</strong> {entrepreneur ? entrepreneur.cnie : ""}</p>
              </div>
              <div>
                <p style={{ margin: "2px 0" }}><strong>I.C.E :</strong> {entrepreneur ? entrepreneur.ice : ""}</p>
                {/* Show IF only if not empty */}
                {entrepreneur && entrepreneur.if && (
                  <p style={{ margin: "2px 0" }}><strong>IF :</strong> {entrepreneur.if}</p>
                )}
                {/* Show TP only if not empty */}
                {entrepreneur && entrepreneur.tp && (
                  <p style={{ margin: "2px 0" }}><strong>TP :</strong> {entrepreneur.tp}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FacteurEditeur;
